"use client";

import { Minus, Plus } from "lucide-react";
import { useState } from "react";

interface NumberInputProps {
  value: number;
  onChange: (val: number) => void;
  min?: number;
  max?: number;
  step?: number;
  label?: string;
  id?: string;
  suffix?: string;
}

export default function NumberInput({
  value,
  onChange,
  min = 0,
  max = 99999999,
  step = 1,
  label,
  id,
  suffix = "",
}: NumberInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  
  const handleDecrement = () => {
    const newValue = Math.max(min, value - step);
    onChange(newValue);
  };

  const handleIncrement = () => {
    const newValue = Math.min(max, value + step);
    onChange(newValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputVal = e.target.value;
    if (inputVal === "") {
      onChange(min);
      return;
    }

    let num = parseFloat(inputVal);
    if (isNaN(num)) return;

    if (num < min) num = min;
    if (num > max) num = max;

    onChange(num);
  };

  return (
    <div className="number-input-wrapper">
      {label && <label htmlFor={id} className="input-label">{label}</label>}
      <div className={`number-input-container ${isFocused ? 'focused' : ''}`}>
        <button
          type="button"
          onClick={handleDecrement}
          disabled={value <= min}
          className="stepper-btn decrement-btn"
          aria-label="Decrease value"
        >
          <Minus size={16} strokeWidth={2.5} />
        </button>
        <div className="input-wrapper">
          <input
            type="number"
            id={id}
            value={value || ""}
            onChange={handleInputChange}
            onFocus={() => setIsFocused(true)}
            onBlur={() => setIsFocused(false)}
            className="number-input-field"
            min={min}
            max={max}
            step={step}
          />
          {suffix && <span className="input-suffix">{suffix}</span>}
        </div>
        <button
          type="button"
          onClick={handleIncrement}
          disabled={value >= max}
          className="stepper-btn increment-btn"
          aria-label="Increase value"
        >
          <Plus size={16} strokeWidth={2.5} />
        </button>
      </div>

      <style jsx>{`
        .number-input-wrapper {
          width: 100%;
          margin-bottom: 0;
        }
        .number-input-container {
          display: flex;
          align-items: stretch;
          background-color: var(--bg-input);
          border: 1px solid var(--border-color);
          border-radius: var(--radius-md);
          overflow: hidden;
          transition: all var(--transition-fast);
          height: 3rem;
        }

        .number-input-container.focused {
          border-color: var(--primary);
          box-shadow: 0 0 0 3px var(--primary-light);
        }
        .stepper-btn {
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 3rem;
          background-color: var(--bg-surface-hover);
          border: none;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all var(--transition-fast);
          flex-shrink: 0;
        }
        .decrement-btn {
          border-right: 1px solid var(--border-color);
        }
        .increment-btn {
          border-left: 1px solid var(--border-color);
        }
        .stepper-btn:hover:not(:disabled) {
          background-color: var(--primary-light);
          color: var(--primary);
        }
        .stepper-btn:active:not(:disabled) {
          background-color: var(--primary-a15);
        }
        .stepper-btn:disabled {
          color: var(--slate-a30);
          cursor: not-allowed;
          background-color: transparent;
        }
        .input-wrapper {
          position: relative;
          flex: 1;
          display: flex;
          align-items: center;
          min-width: 0;
        }
        .number-input-field {
          width: 100%;
          border: none;
          background: transparent;
          color: var(--text-primary);
          font-family: var(--font-body);
          font-size: var(--text-base);
          font-weight: var(--fw-bold);
          text-align: center;
          padding: 0 1rem;
          outline: none;
        }
        .number-input-field::-webkit-outer-spin-button,
        .number-input-field::-webkit-inner-spin-button {
          -webkit-appearance: none;
          margin: 0;
        }
        .number-input-field {
          -moz-appearance: textfield;
        }
        .input-suffix {
          position: absolute;
          right: 1rem;
          color: var(--text-muted);
          font-size: var(--text-2xs);
          font-weight: var(--fw-extrabold);
          text-transform: uppercase;
          letter-spacing: 0.05em;
          pointer-events: none;
        }
      `}</style>
    </div>
  );
}
